import {
  communityStrength,
  contentCategoryList,
  defaultRadioOptions,
  sellCoursesOnlineOptions,
} from "@/shared/constants/marketing.constant";
import useGlobalStore from "@/shared/store/GraphyHome/useGlobalStore";
import {
  faCheck,
  faChevronDown,
  faCircle,
} from "@fortawesome/pro-light-svg-icons";
import { faCircle as faCircleSolid } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import axios from "axios";
// import Alert from "components/elements/Alert";
import { useRouter } from "next/router";
import React, { ChangeEvent, useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
// import { Button } from "src/molecules";
import Button from "@/components/atoms/Button";
import { Modal } from "@/components/molecules/Modal";
import { MARKETING_PAGE_EVENTS } from "@/constants/amplitude.constants";
import { QUERY_PARAMS } from "@/shared/constants/storage.constants";
import Amplitude from "@/shared/services/amplitude.service";
import { getCurrentURL, getScreenWidth } from "@/shared/utils/commons";
import { getFromLocal } from "@/shared/utils/storage.util";
import { getUTMFieldsFromLocalStorage } from "@/shared/utils/utm.util";
import { Select } from "@graphy/ui";
import classNames from "classnames";
import Heading from "../atoms/Heading";
// import Select, { SelectListItem } from "src/molecules/Select";

// const AlertComp = Alert as any;
type SelectListItem = {
  label: string;
  value: string;
};

export interface RadioItem {
  label: string;
  value: string;
}
interface RadioProps {
  id: string;
  list: RadioItem[];
  selected?: RadioItem | null;
  onSelect: (item?: RadioItem) => void;
}

const Radio: React.FC<RadioProps> = ({ id, list, selected, onSelect }) => {
  const getId = useCallback(
    (itemVal: any) => {
      const suffix = itemVal.toString().replace(/ /g, "");
      return `${id}_${suffix}`;
    },
    [id]
  );

  const renderRadioIcon = useCallback(
    (item: RadioItem) => {
      if (!selected || selected.value !== item.value)
        return (
          <FontAwesomeIcon
            icon={faCircle}
            className="absolute left-1/2  top-1/2 w-3 -translate-x-1/2 -translate-y-1/2 transform"
          />
        );
      return (
        <React.Fragment>
          <FontAwesomeIcon
            icon={faCircle}
            className="absolute left-1/2 top-1/2  w-3 -translate-x-1/2 -translate-y-1/2 transform text-purple-3"
          />
          <FontAwesomeIcon
            icon={faCircleSolid}
            className="absolute left-1/2 top-1/2 w-1.5 -translate-x-1/2 -translate-y-1/2 transform text-purple-3"
          />
        </React.Fragment>
      );
    },
    [selected]
  );

  const onChangeHandler = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const found = list?.find((item) => item.value === e.target.value);
      if (onSelect && typeof onSelect === "function") {
        onSelect(found);
      }
    },
    [list, onSelect]
  );

  return (
    <div className="flex flex-wrap gap-3">
      {list?.map((item) => {
        const id = getId(item.value);
        return (
          <div key={id}>
            <label
              role="button"
              htmlFor={id}
              className={classNames(
                "flex items-center whitespace-nowrap rounded-lg border border-grey-2 py-2.5 pl-3 pr-4 text-sm text-slate02 transition-all hover:bg-grey-1",
                {
                  "border-purple-3": selected && selected.value === item.value,
                  "hover:border-grey-3":
                    selected && selected.value !== item.value,
                }
              )}
            >
              <div className="relative mr-2 h-3 w-3">
                {renderRadioIcon(item)}
              </div>
              <div className="text-body-14">{item.label}</div>
            </label>
            <input
              id={id}
              type="radio"
              className="hidden"
              value={item.value}
              checked={item.value === selected?.value}
              onChange={onChangeHandler}
            />
          </div>
        );
      })}
    </div>
  );
};

interface FormHeadingProps {
  heading: string;
}

const FormHeading: React.FC<FormHeadingProps> = ({ heading }) => {
  return <div className="mb-3 text-sm text-slate01">{heading}</div>;
};

interface ScheduleDemoSuccessModalProps {}

const ScheduleDemoSuccessModal: React.FC<
  ScheduleDemoSuccessModalProps
> = () => {
  const {
    showScheduleDemoSuccessModal,
    setShowScheduleDemoSuccessModal,
    scheduleDemoSuccessEmail,
    scheduleDemoSuccessName,
    setScheduleDemoSuccessEmail,
    setScheduleDemoSuccessName,
    scenesKeyAvailable,
  } = useGlobalStore();

  const [courseOnline, setCourseOnline] = useState<RadioItem | undefined>(
    sellCoursesOnlineOptions[0]
  );
  const [selectedContentCategory, setSelectedContentCategory] =
    useState<RadioItem | null>();
  const [selectedCommunityStrength, setSelectedCommunityStrength] =
    useState<RadioItem | null>();
  const [hasBrandedWebsite, setHasBrandedWebsite] =
    useState<RadioItem | null>();
  const [isStartingCourseIn3Months, setIsStartingCourseIn3Months] =
    useState<RadioItem | null>();

  const [loading, setLoading] = useState(false);

  const router = useRouter();

  const reset = useCallback(() => {
    if (router.query.schedule_demo) {
      delete router.query.schedule_demo;
      router.push(router);
    }
    setScheduleDemoSuccessEmail("");
    setScheduleDemoSuccessName("");
    setSelectedCommunityStrength(null);
    setSelectedContentCategory(null);
    setIsStartingCourseIn3Months(null);
    setHasBrandedWebsite(null);
    setCourseOnline(sellCoursesOnlineOptions[0]);
  }, [router]);

  const onClose = useCallback(() => {
    const { utm_params, current_url, screen_width } =
      MARKETING_PAGE_EVENTS.ADDITIONAL_DETAILS_MODAL_DISMISSED.PROPERTIES;
    new Amplitude().sendEvent({
      type: MARKETING_PAGE_EVENTS.ADDITIONAL_DETAILS_MODAL_DISMISSED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
      } as { [key: string]: string },
    });
    reset();
    setShowScheduleDemoSuccessModal(false);
  }, [reset]);

  const selectedItemRenderer = (
    selected?: SelectListItem[] | SelectListItem | null
  ) => {
    return (
      <div className="flex items-center justify-between gap-2.5 rounded-lg border border-slateStroke py-2.5 pl-4 pr-3 text-darkSlate01">
        <div className="text-body-14 text-grey-3">
          {(selected as SelectListItem)?.label || "Choose option"}
        </div>
        <div>
          {" "}
          <FontAwesomeIcon icon={faChevronDown} />{" "}
        </div>
      </div>
    );
  };

  const listItemRenderer = (
    item: SelectListItem,
    selected?: SelectListItem[] | SelectListItem | null
  ) => {
    if (!selected || (selected as SelectListItem)?.value !== item.value)
      return (
        <div
          role="button"
          className="w-100 max-w-md px-4 py-2.5 text-sm text-darkSlate01"
        >
          {item.label}
        </div>
      );
    return (
      <div
        role="button"
        className="w-100 flex max-w-md items-center justify-between bg-selection01 px-4 py-2.5 text-sm text-black"
      >
        <div className="mr-8">{item.label}</div>
        <div>
          <FontAwesomeIcon icon={faCheck} />
        </div>
      </div>
    );
  };

  const sectionClass = "mb-7";

  const isSubmitDisabled = useMemo(() => {
    if (!courseOnline) return true;
    if (courseOnline.value === "yes") {
      if (!selectedContentCategory) return true;
      if (!selectedCommunityStrength) return true;
      if (!hasBrandedWebsite) return true;
    } else if (courseOnline.value === "no") {
      if (!isStartingCourseIn3Months) return true;
    } else {
      if (!selectedContentCategory) return true;
      if (!selectedCommunityStrength) return true;
    }
    return false;
  }, [
    courseOnline,
    selectedCommunityStrength,
    selectedContentCategory,
    isStartingCourseIn3Months,
    hasBrandedWebsite,
  ]);

  const isStartingOnline3MonthsRadioOptions = useMemo(
    () => [...defaultRadioOptions],
    [defaultRadioOptions]
  );
  const hasBrandedWebsiteRadioOptions = useMemo(
    () => [...defaultRadioOptions],
    [defaultRadioOptions]
  );

  const submitHandler = useCallback(async () => {
    const {
      utm_params,
      current_url,
      screen_width,
      currently_selling,
      category,
      social_media_size,
      existing_website,
    } = MARKETING_PAGE_EVENTS.ADDITIONAL_DETAILS_SAVED.PROPERTIES;
    new Amplitude().sendEvent({
      type: MARKETING_PAGE_EVENTS.ADDITIONAL_DETAILS_SAVED.LABEL,
      properties: {
        [current_url]: getCurrentURL(),
        [screen_width]: getScreenWidth(),
        [utm_params]: getUTMFieldsFromLocalStorage(),
        [currently_selling]: courseOnline?.value,
        [category]: selectedContentCategory?.value,
        [social_media_size]: selectedCommunityStrength?.value,
        [existing_website]: hasBrandedWebsite?.value,
      } as { [key: string]: string },
    });
    let payload: { [key: string]: string } = {
      email: scheduleDemoSuccessEmail || "",
      sells_courses_online: courseOnline?.value || "",
    };

    const queryParams = getFromLocal(QUERY_PARAMS);
    if (queryParams && typeof queryParams === "object" && queryParams.gclid) {
      payload.gclid = queryParams.gclid;
    }

    if (courseOnline?.value === "yes") {
      payload.content_category = selectedContentCategory?.value || "";
      payload.community_size = selectedCommunityStrength?.value || "";
      payload.has_a_branded_website = hasBrandedWebsite?.value || "";
    } else if (courseOnline?.value === "no") {
      payload.will_launch_branded_website =
        isStartingCourseIn3Months?.value || "";
    } else {
      payload.content_category = selectedContentCategory?.value || "";
      payload.community_size = selectedCommunityStrength?.value || "";
    }
    setLoading(true);
    const url = scenesKeyAvailable ? scenesKeyAvailable : "/api/hubspot";
    axios
      .post(url, payload)
      .then(() => {
        // toast.success(<AlertComp message="Submitted successfully." />);
        toast.success("Submitted successfully.");
        setLoading(false);
        setShowScheduleDemoSuccessModal(false);
        reset();
      })
      .catch(() => {
        setLoading(false);
      });
  }, [
    scheduleDemoSuccessEmail,
    courseOnline?.value,
    selectedContentCategory?.value,
    selectedCommunityStrength?.value,
    hasBrandedWebsite?.value,
    isStartingCourseIn3Months?.value,
    setShowScheduleDemoSuccessModal,
    reset,
  ]);

  return (
    <Modal showModal={showScheduleDemoSuccessModal} onClose={onClose}>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-2">
          <Heading
            heading={[
              { text: "Thank you " },
              { text: scheduleDemoSuccessName || "", highlight: true },
            ]}
            as="h4"
            fontStyle="text-title-20 lg:text-title-24"
          />
          <p className="text-body-14 text-grey-3">
            Your request has been registered with us! Our experts will get in
            touch with you within next 24 hours.
          </p>
        </div>

        <div className="flex flex-col gap-6">
          {/* Form: */}
          <div className="flex flex-col gap-3">
            <label htmlFor="sellCoursesOnline" className="text-title-14">
              Do you sell digital content online?
            </label>
            {/* <FormHeading heading="Do you sell digital content online?" /> */}
            <Radio
              id="sellCoursesOnline"
              list={sellCoursesOnlineOptions}
              selected={courseOnline}
              onSelect={(item) => {
                setCourseOnline(item);
              }}
            />
          </div>
          {(courseOnline?.value === "yes" ||
            courseOnline?.value === "Looking to start") && (
            <React.Fragment>
              <div className="flex flex-col gap-3">
                <label htmlFor="category" className="text-title-14">
                  Which category does your content fall into?
                </label>
                <Select
                  inputClassName="text-red-0 batman"
                  renderedTextClassName="superman"
                  selected={selectedContentCategory}
                  className="w-full"
                  buttonClassName="w-full justify-start"
                  list={contentCategoryList}
                  selectedItemRenderer={selectedItemRenderer}
                  listItemRenderer={listItemRenderer}
                  // isMaxZIndex
                  itemSelectedCallback={(item) => {
                    setSelectedContentCategory(item);
                  }}
                />
                {/* <Select
                    id="category"
                    selected={selectedContentCategory}
                    className="w-full"
                    buttonClassName="w-full justify-start"
                    list={contentCategoryList}
                    selectedItemRenderer={selectedItemRenderer}
                    listItemRenderer={listItemRenderer}
                    isMaxZIndex
                    itemSelectedCallback={(item) => {
                      setSelectedContentCategory(item);
                    }}
                  /> */}
              </div>
              <div className="flex flex-col gap-3">
                <label htmlFor="communitySize" className="text-title-14">
                  How big is your social media community?
                </label>
                <Select
                  // id="communitySize"
                  selected={selectedCommunityStrength}
                  className="w-full"
                  buttonClassName="w-full justify-start"
                  list={communityStrength}
                  selectedItemRenderer={selectedItemRenderer}
                  listItemRenderer={listItemRenderer}
                  // isMaxZIndex
                  itemSelectedCallback={(item) => {
                    setSelectedCommunityStrength(item);
                  }}
                />
              </div>
            </React.Fragment>
          )}

          {courseOnline?.value === "yes" && (
            <div className="flex flex-col gap-3">
              <label htmlFor="brandedWebsite" className="text-title-14">
                Do you have a branded website?
              </label>
              <Radio
                id="brandedWebsite"
                list={hasBrandedWebsiteRadioOptions}
                selected={hasBrandedWebsite}
                onSelect={(item) => {
                  setHasBrandedWebsite(item);
                }}
              />
            </div>
          )}

          {courseOnline?.value === "no" && (
            <div className="flex flex-col gap-3">
              <label htmlFor="isStartingIn3Months" className="text-title-14">
                Do you plan on starting a course website in the next 3 months?
              </label>
              <Radio
                id="isStartingIn3Months"
                list={isStartingOnline3MonthsRadioOptions}
                selected={isStartingCourseIn3Months}
                onSelect={(item) => {
                  setIsStartingCourseIn3Months(item);
                }}
              />
            </div>
          )}

          <Button onClick={submitHandler}>Save details</Button>
        </div>
      </div>
    </Modal>
  );
};

ScheduleDemoSuccessModal.displayName = "ScheduleDemoSuccessModal";

export default ScheduleDemoSuccessModal;
